import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, PlayCircle } from "lucide-react";
import { api, resolveMedia } from "../../lib/api";
import { inputCls, errMsg, CategorySelect, UploadPicker } from "./shared";

const EMPTY = { title: "", description: "", url: "", platform: "youtube", thumbnail: "", category: "", order: 0, active: true };

export default function VideoManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/admin/videos").then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/videos/${editing}`, form);
        toast.success("वीडियो अपडेट हुआ");
      } else {
        await api.post("/admin/videos", { ...form, order: items.length });
        toast.success("वीडियो जोड़ा गया — वेबसाइट पर दिखने लगा");
      }
      setForm(EMPTY);
      setEditing(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("क्या आप यह वीडियो हटाना चाहते हैं?")) return;
    try {
      await api.delete(`/admin/videos/${id}`);
      setItems((p) => p.filter((i) => i.id !== id));
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const move = async (idx, dir) => {
    const swap = items[idx + dir];
    if (!swap) return;
    const a = items[idx];
    try {
      await api.put(`/admin/videos/${a.id}`, { ...a, order: swap.order });
      await api.put(`/admin/videos/${swap.id}`, { ...swap, order: a.order });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const toggleActive = async (v) => {
    try {
      await api.put(`/admin/videos/${v.id}`, { ...v, active: !v.active });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div>
      <button data-testid="video-new" onClick={() => { setForm(EMPTY); setEditing(null); setShowForm(!showForm); }} className="mb-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ngo-deep text-white font-bold hover:bg-ngo-forest transition-colors duration-300">
        <Plus size={16} /> नया वीडियो जोड़ें
      </button>

      {showForm && (
        <form data-testid="video-form" onSubmit={save} className="bg-white rounded-2xl border border-ngo-deep/10 p-6 mb-8 grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">वीडियो शीर्षक *</label>
            <input data-testid="video-title" required className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">प्लेटफॉर्म</label>
            <select data-testid="video-platform" className={inputCls} value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>
              <option value="youtube">YouTube</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="other">अन्य</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">वीडियो URL * (YouTube लिंक)</label>
            <input data-testid="video-url" required className={inputCls} value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">श्रेणी</label>
            <CategorySelect testid="video-category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} allowEmpty />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">थंबनेल (खाली छोड़ें तो YouTube का अपने-आप लगेगा)</label>
            <div className="flex gap-2 items-center">
              <input data-testid="video-thumbnail" className={inputCls} value={form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} />
              <UploadPicker testid="video-thumbnail-upload" context="videos" label="अपलोड" onUploaded={(url) => setForm((p) => ({ ...p, thumbnail: url }))} />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">संक्षिप्त विवरण</label>
            <input data-testid="video-description" className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <label className="flex items-center gap-2.5 text-sm font-semibold text-ngo-ink">
            <input data-testid="video-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-[18px] h-[18px] accent-ngo-deep" />
            वेबसाइट पर दिखाएं (सक्रिय)
          </label>
          <div className="sm:col-span-2 flex gap-3">
            <button data-testid="video-save" className="px-7 py-3 rounded-full bg-ngo-deep text-white font-bold hover:bg-ngo-forest transition-colors duration-300">{editing ? "अपडेट करें" : "वीडियो जोड़ें"}</button>
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="px-6 py-3 rounded-full border border-ngo-deep/20 text-ngo-deep text-sm font-semibold">रद्द करें</button>
          </div>
        </form>
      )}

      {items.length === 0 && <p data-testid="video-empty" className="text-center text-ngo-ink/50 py-14">अभी कोई वीडियो नहीं है।</p>}
      <div data-testid="video-list" className="space-y-4">
        {items.map((v, idx) => (
          <div key={v.id} className="bg-white rounded-2xl border border-ngo-deep/10 p-4 flex flex-wrap items-center gap-4">
            <div className="w-24 h-16 rounded-lg bg-ngo-forest flex items-center justify-center text-ngo-gold overflow-hidden">
              {v.thumbnail ? <img src={resolveMedia(v.thumbnail)} alt={v.title} className="w-full h-full object-cover" /> : <PlayCircle size={24} />}
            </div>
            <div className="flex-1 min-w-[200px]">
              <p className="font-semibold text-ngo-ink">{v.title}</p>
              <p className="text-xs text-ngo-ink/50 mt-1">{v.active ? <span className="text-ngo-deep font-bold">● सक्रिय</span> : <span className="text-ngo-ink/40 font-bold">● निष्क्रिय</span>} • {v.platform}{v.category && ` • ${v.category}`}</p>
            </div>
            <div className="flex gap-1.5">
              <button data-testid={`video-up-${v.id}`} onClick={() => move(idx, -1)} disabled={idx === 0} aria-label="ऊपर" className="w-9 h-9 rounded-full bg-ngo-mint text-ngo-deep flex items-center justify-center disabled:opacity-30 hover:bg-ngo-leaf/25 transition-colors duration-200"><ArrowUp size={15} /></button>
              <button data-testid={`video-down-${v.id}`} onClick={() => move(idx, 1)} disabled={idx === items.length - 1} aria-label="नीचे" className="w-9 h-9 rounded-full bg-ngo-mint text-ngo-deep flex items-center justify-center disabled:opacity-30 hover:bg-ngo-leaf/25 transition-colors duration-200"><ArrowDown size={15} /></button>
              <button data-testid={`video-toggle-${v.id}`} onClick={() => toggleActive(v)} className={`px-4 h-9 rounded-full text-xs font-bold transition-colors duration-200 ${v.active ? "bg-ngo-deep text-white" : "bg-ngo-ink/10 text-ngo-ink/60"}`}>{v.active ? "सक्रिय" : "निष्क्रिय"}</button>
              <button data-testid={`video-edit-${v.id}`} onClick={() => { setEditing(v.id); setForm({ title: v.title, description: v.description || "", url: v.url, platform: v.platform || "youtube", thumbnail: v.thumbnail || "", category: v.category || "", order: v.order, active: v.active }); setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); }} aria-label="संपादित करें" className="w-9 h-9 rounded-full bg-ngo-mint text-ngo-deep flex items-center justify-center hover:bg-ngo-leaf/25 transition-colors duration-200"><Pencil size={15} /></button>
              <button data-testid={`video-delete-${v.id}`} onClick={() => remove(v.id)} aria-label="हटाएं" className="w-9 h-9 rounded-full bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-100 transition-colors duration-200"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
